// src/lib/adsIdbHelper.ts
/**
 * 広告配信用にIndexedDBを扱うためのヘルパーモジュール
 */
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Ad } from '../types/index';
import { logger } from '../utils/logger'; // loggerがあるとデバッグに便利です

const DB_NAME = 'ad-cache-db';
const DB_VERSION = 1;
const STORE_NAME = 'ads';
const CACHE_EXPIRE_DAYS = 1; // キャッシュ有効期限（1日）

// Adオブジェクトにキャッシュ期限を追加した型
type CachedAd = Ad & {
    expiresAt: number;
};

// データベースのスキーマ定義
interface AdCacheDB extends DBSchema {
    [STORE_NAME]: {
        key: string;
        value: CachedAd;
    };
}

// データベース接続を管理するシングルトン
let dbPromise: Promise<IDBPDatabase<AdCacheDB>> | null = null;

/**
 * データベースへの接続を初期化し、シングルトンとして管理する
 */
const getDb = (): Promise<IDBPDatabase<AdCacheDB>> => {
    if (!dbPromise) {
        dbPromise = openDB<AdCacheDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            },
        });
    }
    return dbPromise;
};

/**
 * キャッシュの有効期限を計算する
 * @returns {number} 1日後のタイムスタンプ (ミリ秒)
 */
const getExpiryTimestamp = (): number => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CACHE_EXPIRE_DAYS);
    return expiryDate.getTime();
};

/**
 * 複数の広告をIndexedDBに一括で保存（追加または更新）します
 * @param ads - 保存する広告データの配列
 */
export const saveAdsToDB = async (ads: Ad[]): Promise<void> => {
    try {
        const db = await getDb();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const expiry = getExpiryTimestamp();

        // Promise.allで並列に書き込み処理を行う
        await Promise.all(
            ads.map(ad => {
                const cachedAd: CachedAd = { ...ad, expiresAt: expiry };
                return tx.store.put(cachedAd);
            })
        );

        await tx.done; // トランザクションの完了を待つ
        logger.log(`${ads.length} ads saved to IndexedDB.`);
    } catch (error) {
        logger.error(`Failed to save ads to IndexedDB:`, error);
    }
};

/**
 * IndexedDBから全ての広告を取得します。
 * オンライン時はキャッシュの有効期限をチェックし、期限切れの場合はデータを削除します。
 * @returns {Promise<Ad[] | null>} 広告データの配列、または存在しない/期限切れの場合はnull
 */
export const loadAdsFromDB = async (): Promise<Ad[] | null> => {
    try {
        const db = await getDb();
        const cachedAds = await db.getAll(STORE_NAME);

        if (!cachedAds || cachedAds.length === 0) {
            return null; // キャッシュが存在しない
        }

        const isOnline = navigator.onLine;
        const isCacheExpired = Date.now() > cachedAds[0].expiresAt;

        // オンラインかつキャッシュの期限が切れている場合
        if (isOnline && isCacheExpired) {
            logger.log('Ad cache expired. Clearing IndexedDB.');
            // 非同期で古いキャッシュをクリア
            db.clear(STORE_NAME).catch(err => logger.error('Failed to clear expired ads:', err));
            return null;
        }

        // 有効なキャッシュ、またはオフラインで期限切れのキャッシュを返す
        logger.log(`Using ad cache from IndexedDB. Expired: ${isCacheExpired}, Online: ${isOnline}`);
        
        // `expiresAt`プロパティを除いた純粋なAdオブジェクトの配列を返す
        return cachedAds.map(({ expiresAt, ...adData }) => adData as Ad);

    } catch (error) {
        logger.error(`Failed to load ads from IndexedDB:`, error);
        return null;
    }
};